<script lang="ts">
export default {
  name: 'Step5PolicyDetails',
};
</script>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, reactive, ref, watchEffect } from 'vue';
import { useDataReadinessWizard } from '@/composables/useDataReadinessWizard';
import { useToast } from '@/composables/useToast';
import PolicyRuleAccordion from '@/components/v2/data_readiness/PolicyRuleAccordion.vue';
import ConsolidatedEditPolicyModal from '@/components/v2/policy/ConsolidatedEditPolicyModal.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import { accountService } from '@/services/api';
import type { PolicyRuleViolationFlags } from '@/composables/usePolicyEditPending';
import { GetPoliciesAsHierarchy, type PolicyWithChildren } from '@/utilities/Policies';
import { hasPolicyRuleViolation } from '@/utilities/dataReadinessIssues';

const wizard = useDataReadinessWizard();
const toast = useToast();

// Initial load only — a refetch with data already on screen keeps it visible.
const isLoading = computed(() => wizard.loadingRules.value && wizard.ruleViolatingPolicies.value.length === 0);

// The consolidated edit modal needs the full policy (with child lines) as its
// anchor, so load the account's policy hierarchy alongside the rule data.
// Same fixed filters as the rest of the wizard: current, CAC-only policies.
const policyHierarchy = ref<PolicyWithChildren[]>([]);
const loadingHierarchy = ref(false);

async function loadPolicyHierarchy() {
  loadingHierarchy.value = true;
  try {
    const policies = await accountService.policiesById(wizard.accountId.value, false, false, false);
    policyHierarchy.value = GetPoliciesAsHierarchy(policies);
  } catch {
    policyHierarchy.value = [];
  } finally {
    loadingHierarchy.value = false;
  }
}

onBeforeMount(() => Promise.all([wizard.refreshRuleViolatingPolicies(), loadPolicyHierarchy()]));

const policies = computed(() => wizard.ruleViolatingPolicies.value.filter(hasPolicyRuleViolation));

const openPolicyId = ref<number | null>(null);
function toggle(policyId: number) {
  openPolicyId.value = openPolicyId.value === policyId ? null : policyId;
}

const editPolicy = reactive({
  show: false,
  policy: null as PolicyWithChildren | null,
  policySourceId: 0,
  ruleViolations: {} as PolicyRuleViolationFlags,
});
function handleEdit(policyId: number) {
  // Client-readiness policyId is PolicyDetailSnapshot.PolicyID, which is
  // uniqPolicy on the policy hierarchy.
  const anchor = policyHierarchy.value.find((p) => String(p.uniqPolicy) === String(policyId));
  if (!anchor) {
    if (loadingHierarchy.value) {
      toast.info('Policies are still loading — try again in a moment.');
    } else {
      toast.error('Unable to load this policy for editing.');
    }
    return;
  }
  const row = policies.value.find((p) => p.policyId === policyId);
  editPolicy.ruleViolations = {
    noLimit: !!row?.noLimitRule,
    invalidRetention: !!row?.invalidRetentionRule,
    invalidAttachment: !!row?.invalidAttachmentPointRule,
  };
  editPolicy.policy = anchor;
  editPolicy.policySourceId = anchor.policySourceID || 0;
  editPolicy.show = true;
}
async function handleSave() {
  editPolicy.show = false;
  await Promise.all([wizard.refreshRuleViolatingPolicies(), loadPolicyHierarchy()]);
}

watchEffect(() => {
  const n = policies.value.length;
  if (isLoading.value) {
    wizard.setFooterHint({ text: 'Checking policies…', color: 'muted' });
  } else if (n === 0) {
    wizard.setFooterHint({ text: 'All policy rules clear — ready to proceed', color: 'green' });
  } else {
    wizard.setFooterHint({
      text: `${n} polic${n === 1 ? 'y needs' : 'ies need'} correction`,
      color: 'amber',
    });
  }
});

onBeforeUnmount(() => wizard.setFooterHint(null));
</script>

<template>
  <div class="p-6">
    <h2 class="mb-1 text-base font-semibold">Policy details</h2>
    <p class="mb-4 text-sm text-base-content/60">
      Policies below violate one or more rules. Open a policy and click Edit Policy to correct it.
    </p>

    <div
      v-if="isLoading"
      class="overflow-hidden rounded-md border border-base-300">
      <div
        v-for="i in 3"
        :key="`skeleton-${i}`"
        class="flex items-center gap-3 border-b border-base-300 px-4 py-3 last:border-b-0">
        <Skeleton
          shape="circle"
          size="sm" />
        <div class="flex flex-col gap-1.5">
          <Skeleton width="10rem" />
          <Skeleton
            width="16rem"
            height="0.6rem" />
        </div>
      </div>
    </div>
    <div
      v-else-if="policies.length === 0"
      class="rounded-md border border-base-300 bg-base-100 px-4 py-8 text-center text-sm text-base-content/60">
      No policies with rule violations.
    </div>
    <div
      v-else
      class="overflow-hidden rounded-md border border-base-300">
      <PolicyRuleAccordion
        v-for="p in policies"
        :key="p.policyId"
        :policy="p"
        :is-open="openPolicyId === p.policyId"
        @toggle="toggle(p.policyId)"
        @edit="handleEdit" />
    </div>

    <ConsolidatedEditPolicyModal
      v-if="editPolicy.policy"
      :show="editPolicy.show"
      :policy="editPolicy.policy"
      :policy-source-id="editPolicy.policySourceId"
      :rule-violations="editPolicy.ruleViolations"
      @close="editPolicy.show = false"
      @save="handleSave" />
  </div>
</template>
